export type RosterReadinessKind =
  | "needsHealthyPlayers"
  | "needsPlayers"
  | "payrollRisk"
  | "ready"
  | "readonly"
  | "tooManyPlayers";

export type RosterReadinessAction = "findPlayers" | "manageRoster" | "reviewOnly" | "reviewPayroll";

export type RosterPrimaryActionSurface = "none" | "hero" | "fixedManagement";

export type RosterReadinessModel = {
  action: RosterReadinessAction;
  healthyCount: number;
  injuredCount: number;
  kind: RosterReadinessKind;
  minimumGap: number;
  openRosterSpots: number;
  severity: "danger" | "info" | "ok" | "warning";
  starterGap: number;
};

export const getRosterPrimaryActionPlan = ({
  action,
  managementAvailable,
}: {
  action: RosterReadinessAction;
  managementAvailable: boolean;
}): RosterPrimaryActionSurface => {
  if (action === "reviewOnly") {
    return "none";
  }

  if (action === "findPlayers") {
    return "hero";
  }

  // Finance is now an in-place Team-tab module rather than a competing hero
  // CTA. Payroll pressure must not displace normal roster management.
  if (action === "reviewPayroll") {
    return managementAvailable ? "fixedManagement" : "none";
  }

  return managementAvailable ? "fixedManagement" : "hero";
};

export const buildRosterReadinessModel = ({
  editable,
  luxuryTaxAmount,
  maxRosterSize,
  minPayrollAmount,
  minRosterSize,
  numPlayersOnCourt,
  playerCount,
  injuredCount,
}: {
  editable: boolean;
  injuredCount: number;
  luxuryTaxAmount: number;
  maxRosterSize: number;
  minPayrollAmount: number;
  minRosterSize: number;
  numPlayersOnCourt: number;
  playerCount: number;
}): RosterReadinessModel => {
  const healthyCount = playerCount - injuredCount;
  const minimumGap = Math.max(0, minRosterSize - playerCount);
  const openRosterSpots = maxRosterSize - playerCount;
  const starterGap = Math.max(0, numPlayersOnCourt - healthyCount);

  if (!editable) {
    return {
      action: "reviewOnly",
      healthyCount,
      injuredCount,
      kind: "readonly",
      minimumGap,
      openRosterSpots,
      severity: "info",
      starterGap,
    };
  }

  if (playerCount > maxRosterSize) {
    return {
      action: "manageRoster",
      healthyCount,
      injuredCount,
      kind: "tooManyPlayers",
      minimumGap,
      openRosterSpots,
      severity: "danger",
      starterGap,
    };
  }

  if (playerCount < minRosterSize) {
    return {
      action: "findPlayers",
      healthyCount,
      injuredCount,
      kind: "needsPlayers",
      minimumGap,
      openRosterSpots,
      severity: "danger",
      starterGap,
    };
  }

  if (starterGap > 0) {
    return {
      action: "manageRoster",
      healthyCount,
      injuredCount,
      kind: "needsHealthyPlayers",
      minimumGap,
      openRosterSpots,
      severity: "warning",
      starterGap,
    };
  }

  if (luxuryTaxAmount > 0 || minPayrollAmount > 0) {
    return {
      action: "reviewPayroll",
      healthyCount,
      injuredCount,
      kind: "payrollRisk",
      minimumGap,
      openRosterSpots,
      severity: "warning",
      starterGap,
    };
  }

  return {
    action: "manageRoster",
    healthyCount,
    injuredCount,
    kind: "ready",
    minimumGap,
    openRosterSpots,
    severity: "ok",
    starterGap,
  };
};
